import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ArrowRightIcon, CircleMinusIcon, InfoIcon, X } from 'lucide-react';
import userSkillsService, { UserSkill, MasterSkill } from '../../services/UserSkillsService';
import GuideTooltip from '../../shared/GuideTooltip';
import { showToast } from '../../shared/Toast';
import MediaKitModal from '../../submission_boards/component/MediaKitModal';
import { KitDocumentType, SbCurrentUser } from '../../submission_boards/component/SbModel';
import {
  KitDocumentModel,
  OverviewKitDocumentModel,
} from '../../submission_kits/components/KitModels';
import overviewKitService from '../../services/OverviewKitService';
import FilePreviewer from '../../submission_kits/components/FilePreviewer';
import { useIsMobile } from '../../submission_boards/component/useIsMobile';
import HelpSkillsModal from './HelpSkillsModal';
import ViewMediaModel from '../../submission_kits/components/ViewMediaModel';

interface OverviewContentProps {
  currentUser: SbCurrentUser;
  fromScreen: string;
  userUuid?: string;
  initialGeneralInfo?: string;
}

export const OverviewContent: React.FC<OverviewContentProps> = ({
  currentUser,
  fromScreen,
  initialGeneralInfo = '',
}) => {
  const [generalInformation, setGeneralInformation] = useState(initialGeneralInfo);
  const [skills, setSkills] = useState<UserSkill[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Search states
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<MasterSkill[]>([]);
  const [searchResultsFiltered, setSearchResultsFiltered] = useState<MasterSkill[]>([]);

  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [selectedCardIndex, setSelectedCardIndex] = useState<number | null>(null);
  const [openUpward, setOpenUpward] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [overviewKitDocuments, setOverviewKitDocuments] = useState<OverviewKitDocumentModel[]>([]);
  const [mediaKitDocuments, setMediaKitDocuments] = useState<KitDocumentModel[]>([]);
  const [showMediaKitModal, setShowMediaKitModal] = useState(false);
  const [selectMediaType, setselectMediaType] = useState(KitDocumentType.Image);
  const [minHeightImage, setMinHeightImage] = useState('10.375rem');
  const [minHeightVideo, setMinHeightVideo] = useState('8.75rem');

  
  const [showSkillsHelpModal, setshowSkillsHelpModal] = useState(false);
  // const [showMediaBig, setshowMediaBig] = useState<KitDocumentModel | null>(null);
  const [removingIds, setRemovingIds] = useState<Set<number>>(new Set());

  const isMobile = useIsMobile();

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (isMobile) {
      setMinHeightImage('10.375rem');
      setMinHeightVideo('8.75rem');
      // setMinHeightImage('18.75rem');
      // setMinHeightVideo('17.5rem');
    } else {
      setMinHeightImage('10.375rem');
      setMinHeightVideo('8.75rem');
    }
  }, [isMobile]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        console.log("reset setShowDropdown")
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search
  // useEffect(() => {
  //   const timeoutId = setTimeout(() => {
  //     if (searchQuery.trim().length >= 2) {
  //       handleSearch();
  //     } else {
  //       setSearchResults([]);
  //       setShowDropdown(false);
  //     }
  //   }, 300);
  //   return () => clearTimeout(timeoutId);
  // }, [searchQuery]);

  const updateDropdownDirection = () => {
    if (searchContainerRef.current) {
      const rect = searchContainerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const dropdownHeight = 240; // max-h-60
      setOpenUpward(spaceBelow < dropdownHeight + 20 && rect.top > dropdownHeight);
    }
  };

  useEffect(() => {
    if (showDropdown) {
      updateDropdownDirection();
      window.addEventListener('scroll', updateDropdownDirection, true);
      window.addEventListener('resize', updateDropdownDirection);
    }
    return () => {
      window.removeEventListener('scroll', updateDropdownDirection, true);
      window.removeEventListener('resize', updateDropdownDirection);
    };
  }, [showDropdown]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const promises = [loadOverview(), fetchOverviewKitDocuments(), handleSearch()];
      await Promise.all(promises);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  const loadOverview = async () => {
    try {
      setLoading(true);
      const response = await userSkillsService.getOverview();
      if (response.success) {
        setGeneralInformation(response.general_information || '');
        setSkills(response.skills || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    // if (!searchQuery.trim()) return;
    try {
      setIsSearching(true);
      const response = await userSkillsService.searchMasterSkills(searchQuery.trim());
      if (response.success) {
        // Filter out already selected skills
        const selectedIds = skills.map((s) => s.master_special_skill_id).filter(Boolean);
        let filtered = response.skills.filter((s) => !selectedIds.includes(s.id));
        filtered = [...filtered].sort((a, b) => {
          const nameComparison = a.name.localeCompare(b.name);
          if (nameComparison === 0) {
            return a.sub_skill.localeCompare(b.sub_skill);
          }
          return nameComparison;
        });
        setSearchResults(filtered);
        setSearchResultsFiltered(filtered);
        // setShowDropdown(filtered.length > 0);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectSkill = async (masterSkill: MasterSkill) => {
    if (skills.length >= 10) return;

    // Check if already selected
    if (skills.some((s) => s.master_special_skill_id === masterSkill.id)) {
      // setShowDropdown(false);
      // setSearchQuery('');
      return;
    }

    // Add to skills with temp ID
    const newSkill: UserSkill = {
      id: Date.now(),
      uuid: '',
      name: masterSkill.sub_skill,
      master_special_skill_id: masterSkill.id,
    };

    setSkills([...skills, newSkill]);
    setSearchQuery('');
    setSearchResultsFiltered([...searchResults]);
    setShowDropdown(false);
    handleSave([...skills, newSkill]);

  };

  const handleSave = async (newSkills: any, shouldRedirect: boolean = false) => {
    if (!generalInformation || !generalInformation.trim()) {
      setValidationError('General Information is required');
      return;
    }
    setValidationError(null);

    try {
      setLoading(true);
      setError(null);

      const response = await userSkillsService.updateOverview(generalInformation.trim(), newSkills || skills);

      if (response.success) {
        showToast('Saved Successfully.', 'success');
        setSkills(response.skills || []);
        setError(null);
        if (fromScreen == 'wizard' && shouldRedirect) {
          // redirect to work wizard
          window.location.href = '/projects';
        }
      }
    } catch (err) {
      setError('Failed to save changes');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    window.history.back();
  };

  const handleDeleteSkill = (skillId: number) => {
    setSkills(skills.filter((s) => s.id !== skillId));
  };

  // Memoize GuideTooltip items to prevent re-renders when typing
  const guideTooltipItems = useMemo(
    () => [
      {
        icon: 'bullet' as const,
        boldText: 'General Information',
        text: 'Add a professional overview that describes your experience and expertise in the industry.',
      },
      {
        icon: 'bullet' as const,
        boldText: 'Specialty Skills',
        text: "Select up to 10 skills that reflect where you’re trusted as a specialist on set.",
      },
    ],
    []
  );

  const fetchOverviewKitDocuments = async () => {
    try {
      setIsLoading(true);
      const response = await overviewKitService.getOverviewKitDocuments('');
      if (response.status === 'success') {
        setOverviewKitDocuments(response.overview_kit_documents || []);
      }
    } catch (error) {
      showToast('Error fetching Overview Kit photos.', 'danger');
    } finally {
      setIsLoading(false);
    }
  };
  const handleMediaSelected = async (kitDocument: KitDocumentModel): Promise<boolean> => {
    if (!kitDocument || !kitDocument.id) {
      showToast('Please select a media file.', 'danger');
      return false;
    }

    try {
      const tag = '';
      await overviewKitService.addInOverviewKitDocument(kitDocument.id, tag, selectedCardIndex);
      await fetchOverviewKitDocuments();
      setSelectedCardIndex(null);
      showToast('Media added successfully to Overview Kit.', 'success');
      return true;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Error adding media to Overview Kit.';
      showToast(errorMessage, 'danger');
      return false;
    }
  };
  const handleRemove = async (overviewKitDocument: OverviewKitDocumentModel) => {
    if (removingIds.has(overviewKitDocument.id)) return; // Prevent double-tap

    try {
      // Add to removing set to show loading state
      setRemovingIds(prev => new Set(prev).add(overviewKitDocument.id));

      // Optimistic update - remove from UI immediately
      setOverviewKitDocuments(prev =>
        prev.filter(doc => doc.id !== overviewKitDocument.id)
      );

      await overviewKitService.deleteFromOverviewKitDocument(overviewKitDocument);
      showToast('Media removed successfully.', 'success');
    } catch (error: any) {
      // Revert on error - refetch to restore state
      await fetchOverviewKitDocuments();
      const errorMessage = error.response?.data?.message || 'Error removing media.';
      showToast(errorMessage, 'danger');
    } finally {
      setRemovingIds(prev => {
        const next = new Set(prev);
        next.delete(overviewKitDocument.id);
        return next;
      });
    }
  };
  const handleCardClick = (index: number, mediaType: number) => {
    setselectMediaType(mediaType);
    setSelectedCardIndex(index);
    setShowMediaKitModal(true);
  };
  const renderCard = (index: number, mediaType: number) => {
    // First slot (index 0) shows only featured image (tag='1')
    // Other slots show non-featured images (tag != '1') in order
    let mainKitDoc;
    const positionDoc = overviewKitDocuments.filter(
      (doc) =>
        doc.position == index &&
        ((mediaType == KitDocumentType.Image &&
          doc.kit_document.file_content_type.startsWith('image')) ||
          (mediaType == KitDocumentType.Video &&
            doc.kit_document.file_content_type.startsWith('video')))
    );
    if (positionDoc.length > 0) {
      mainKitDoc = positionDoc[0];
    }
    const isEmpty = !mainKitDoc;
    // let minHeight = mediaType == KitDocumentType.Image ? '10.375rem' : '8.75rem';
    // if(isMobile) {

    // }
    const cardKey = mainKitDoc ? `${mediaType}-${index}-${mainKitDoc.id}` : `${mediaType}-${index}-empty`;
    return (
      <div
        key={cardKey}
        className="flex flex-col gap-2-1 bg-white rounded-xl overflow-hidden h-full"
        style={{
          // height: mediaType == KitDocumentType.Image ?minHeightImage:minHeightVideo,
        }}
      >
        <div
          className="flex flex-col gap-2 rounded-2xl hover:shadow-ring-0-0-0-2-4 cursor-pointer overflow-hidden"
          style={{
            backgroundColor: '#3C82F60D',
            border: isEmpty ? '0.09375rem dashed #3C82F6' : '0px',
            aspectRatio: mediaType == KitDocumentType.Image ? 4 / 5 : 16 / 9,
          }}
          onClick={() => {
            if (isEmpty) {
              handleCardClick(index, mediaType);
            }
          }}
        >
          {isEmpty ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400" style={{
            }}>
              <svg className="w-12 h-12 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              {/* <span className="text-sm">
                {mediaType == KitDocumentType.Image ? 'Add Photo' : 'Add Video'}
              </span> */}
            </div>
          ) : (
            <div className="relative flex-1">
              <FilePreviewer
                // file_url={
                //   mainKitDoc.kit_document.cropped_file_url || mainKitDoc.kit_document.file_url
                // }
                file_url={
                    mainKitDoc.kit_document.file_content_type.startsWith('image') ? 
                    (mainKitDoc.kit_document.cropped_file_url ??
                    mainKitDoc.kit_document.file_url) : 
                    mainKitDoc.kit_document.file_url
                    
                  }
                  poster_url={
                    mainKitDoc.kit_document.file_content_type.startsWith('image') ? '' : 
                    mainKitDoc.kit_document.cropped_file_url
                  }

                file_name={mainKitDoc.kit_document.file_name}
                file_content_type={mainKitDoc.kit_document.file_content_type}
                showControl={false}
                dynamicHeight={mediaType == KitDocumentType.Image ? true : false}
                object_fit={mediaType == KitDocumentType.Image ? "object-contain object-top bg-gray-100" : "object-cover"}
              />
              <button
                id={'image_id_' + index}
                type="button"
                className="absolute flex items-center justify-center h-6 w-6 top-1 right-1 rounded-full shadow-md hover:scale-110 transition-transform text-white hover:text-black bg-opacity-40 bg-black hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={removingIds.has(mainKitDoc.id)}
                onPointerDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleRemove(mainKitDoc);
                }}
              >
                <div className="h-5 w-5">
                  <CircleMinusIcon className="h-5 w-5" />
                </div>
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };
  return (
    <>
      <div className="w-full flex flex-col gap-10 px-4 sm:p-0 items-center">
        <div className="w-full flex flex-col gap-1" style={{ maxWidth: '45rem' }}>
          <div
            className="w-full text-blue-403 flex flex-row items-center font-medium justify-center sm:justify-start relative"
            style={{ fontSize: '2rem' }}
          >
            About
            <GuideTooltip
              items={guideTooltipItems}
              storageKey="overview_guide"
              containerId="overview_tooltip_container"
            />
          </div>
          <div id="overview_tooltip_container"></div>
        </div>

        <div
          className="flex flex-col sm:flex-row justify-center items-start pb-10 gap-10 w-full"
          style={{ maxWidth: '45rem' }}
        >
          <div className="flex flex-col items-start p-0 gap-8 w-full sm:flex-1">
            {error && (
              <div className="w-full p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <div className="flex flex-col gap-8 w-full">
              {/* GENERAL INFORMATION SECTION */}
              <div className="flex flex-col sm:flex-row gap-5">
                <div className="text-xl font-semibold mb-2" style={{ width: '10.5rem' }}>
                  <span className="block sm:hidden whitespace-nowrap">General Information</span>
                  <span className="hidden sm:block">
                    General<br />Information
                  </span>
                </div>


                <div className="flex flex-col gap-2-1 flex-1 ">
                  <div className="flex flex-row items-start p-1 gap-2-1 border bg-custom-blue focus-within:bg-white transition-colors duration-200 rounded-xl">
                    <div className="flex flex-row items-center w-full">
                      <div className="flex flex-col items-start px-2 py-3 gap-1 w-full">
                        <label className="block text-xs font-medium text-gray-600 mb-1 px-2">
                          Professional Overview*
                        </label>
                        <div className="flex items-center gap-2 w-full">
                          <textarea
                            className="m-1 p-2 bg-transparent outline-none focus:ring-0 focus:border-0 block w-full sm:text-sm border-0 border-gray-300 rounded-md resize-none"
                            value={generalInformation}
                            onChange={(e) => {
                              setGeneralInformation(e.target.value);
                              if (e.target.value.trim()) setValidationError(null);
                            }}
                            onBlur={() => {
                              handleSave(undefined);
                            }}
                            placeholder="Add a short bio…"
                            rows={5}
                            name="overview_notes[notes]"
                            id="overview_notes"
                          ></textarea>
                        </div>
                      </div>
                    </div>
                  </div>
                  {validationError && (
                    <p className="text-sm text-red-600 mt-1">{validationError}</p>
                  )}
                </div>
              </div>

              {/* DIVIDER LINE */}
              <hr className="border-t border-gray-300 w-full" />

              {/* SPECIALTY SKILLS SECTION */}
              <div className="flex flex-col sm:flex-row gap-5">
                <div className="text-xl font-semibold mb-2 flex flex-col gap-1-1" style={{ width: '10.5rem' }}>

                  {/* Mobile view: single line */}
                  <div className="flex sm:hidden flex-row items-center gap-1">
                    <span className="whitespace-nowrap">Specialty Skills</span>

                    <button
                      type="button"
                      onClick={() => setshowSkillsHelpModal(true)}
                      className="flex items-center justify-center text-blue-600 hover:text-blue-800 transition-colors duration-200"
                    >
                      <InfoIcon className="w-5 h-5 text-blue-600" />
                    </button>
                  </div>

                  {/* Desktop view: two lines with icon on first line */}
                  <div className="hidden sm:flex flex-col leading-tight">

                    <div className="flex flex-row items-center gap-12">
                      <span>Specialty</span>

                      <button
                        type="button"
                        onClick={() => setshowSkillsHelpModal(true)}
                        className="flex items-center justify-center text-blue-600 hover:text-blue-800 transition-colors duration-200"
                      >
                        <InfoIcon className="w-5 h-5 text-blue-600" />
                      </button>
                    </div>

                    <span>Skills</span>
                  </div>

                </div>

                <div className="flex flex-col gap-2-1 flex-1">
                  {/* Skills Container */}
                  <div
                    className="p-4 border border-gray-300 rounded-lg bg-custom-blue"
                    ref={dropdownRef}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (searchInputRef) {
                        searchInputRef.current.focus();
                      }
                    }}

                  >
                    {/* Header Text */}
                    <div className="mb-3">
                      <span className="text-xs text-gray-600 font-medium">
                        Select up to 10 specialty skills that represent where you’re a true specialist
                      </span>
                      <div className="text-xs text-gray-500">({skills.length}/10)</div>
                    </div>

                    {/* Skills Tags and Inline Search */}
                    <div className="flex flex-wrap gap-3 items-center relative">
                      {/* Selected Skills Tags */}
                      {skills.map((skill) => (
                        <div
                          key={skill.id}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-gray-300 hover:border-gray-400 transition-colors"
                        >
                          <span className="text-sm text-gray-700">{skill.name}</span>
                          <button
                            onClick={() => handleDeleteSkill(skill.id)}
                            type="button"
                            className="text-gray-400 hover:text-red-500 transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}

                      {/* Inline Search Input */}
                      {skills.length < 10 && (
                        <div className="inline-flex relative" ref={searchContainerRef}>
                          <input
                            ref={searchInputRef}
                            type="text"
                            value={searchQuery}
                            onChange={(e) => {
                              if (searchResults.length > 0) {
                                const value = e.target.value.toLowerCase();
                                setSearchResultsFiltered(prev =>
                                  searchResults.filter(item => item.name.toLowerCase().indexOf(value) >= 0
                                    || item.sub_skill.toLowerCase().indexOf(value) >= 0)
                                );

                                setSearchQuery(e.target.value)
                              }

                            }
                            }
                            onFocus={() => {
                              console.log("set setShowDropdown")
                              setShowDropdown(true);
                              // if (searchResults.length > 0) setShowDropdown(true);
                            }}
                            onBlur={(e) => {
                              // e.stopPropagation();
                              // setShowDropdown(false);
                              // if (searchResults.length > 0) setShowDropdown(true);
                            }}
                            placeholder={skills.length === 0 ? 'Search skills...' : ''}
                            className="text-sm bg-transparent border-0 outline-none focus:ring-0 focus:border-0 text-gray-700 placeholder-gray-400 w-full min-w-24"
                          />
                          {isSearching && (
                            <div className="animate-spin w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full ml-2" />
                          )}

                          {/* Search Results Dropdown */}
                          {showDropdown && searchResultsFiltered.length > 0 && (
                            <div
                              className={`absolute z-10 bg-white rounded-lg max-h-60 overflow-y-auto p-3 primary-color ${openUpward ? 'bottom-full mb-2' : 'mt-10'
                                }`}
                              style={{
                                border: '0.0625rem solid #374151',
                                minWidth: '17.5rem',
                              }}
                            >
                              {searchResultsFiltered.map((skill, index) => {
                                let showName = false;
                                if (index > 0) {
                                  if (skill.name !== searchResultsFiltered[index - 1].name) {
                                    showName = true;
                                  }
                                } else {
                                  showName = true;
                                }
                                return (
                                  <>
                                    {showName && (
                                      <div
                                        key={index}
                                        className="flex flex-row items-center px-2 py-2 gap-2-1 rounded bg-grayish-blue-80"
                                        style={{}}
                                      >
                                        <div className="flex flex-col ">
                                          <span className="text-base font-bold uppercase primary-color">
                                            {skill.name}
                                          </span>
                                        </div>
                                      </div>
                                    )}
                                    <div
                                      key={skill.id}
                                      onClick={async (e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        await handleSelectSkill(skill)
                                      }}
                                      className="flex items-center justify-start px-4 py-1 hover:bg-blue-50 cursor-pointer gap-1-1"
                                    >
                                      <div className="w-4 h-4">
                                        <ArrowRightIcon className="w-4 h-4" />
                                      </div>
                                      <div className="text-sm font-medium text-gray-800">
                                        {skill.sub_skill}
                                      </div>
                                      {skill.count && (
                                        <span className="text-sm text-gray-400">
                                          ({skill.count})
                                        </span>
                                      )}
                                    </div>
                                  </>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {/* DIVIDER LINE */}
              <hr className="border-t border-gray-300 w-full" />

              <div className="flex flex-col sm:flex-row gap-5">
                <div className="text-xl font-semibold mb-2 flex" style={{ width: '10.5rem' }}>
                  About Media
                </div>
                <div className="flex flex-col gap-2-1 flex-1">
                  {/* media */}
                  <div className="grid grid-cols-1 gap-5" style={{}}>
                    {/* photos */}
                    <div className="flex flex-col flex-1">
                      <div className="flex flex-col  flex-1 font-medium text-lg ">Photos</div>
                      <div className="flex flex-col  flex-1 text-xs">Add Up to 6 files</div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-1-1">
                      {Array.from({ length: 6 }).map((_, index) => {
                        return renderCard(index, KitDocumentType.Image);
                      })}
                    </div>
                    {/* videos */}
                    <div className="flex flex-col flex-1">
                      <div className="flex flex-col  flex-1 font-medium text-lg ">Videos</div>
                      <div className="flex flex-col  flex-1 text-xs">Add Up to 4 files</div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-2 gap-1-1">
                      {Array.from({ length: 4 }).map((_, index) => {
                        return renderCard(index, KitDocumentType.Video);
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ACTION BUTTONS with border like Account page - OUTSIDE all content divs */}
      <div
        className="flex justify-center w-full border-t border-gray-200"
        style={{ paddingTop: '1.75rem', paddingBottom: '0' }}
      >
        <div className="flex justify-center w-full">
          {fromScreen !== 'wizard' && <button
            onClick={handleCancel}
            type="button"
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Cancel
          </button>}
          {fromScreen == 'wizard' ? <button
            onClick={() => {
              handleSave(undefined, true);
            }}
            type="button"
            disabled={loading}
            className="inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-500 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer ml-3 disabled:opacity-50"
          >
            {loading ? (
              <>
                Continue
                <svg
                  className="animate-spin ml-3 h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              </>
            ) : (
              'Continue'
            )}
          </button> : <button
            onClick={() => {
              handleSave(undefined, true);
            }}
            type="button"
            disabled={loading}
            className="inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-500 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer ml-3 disabled:opacity-50"
          >
            {loading ? (
              <>
                Save
                <svg
                  className="animate-spin ml-3 h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              </>
            ) : (
              'Save'
            )}
          </button>}
        </div>
      </div>
      {showMediaKitModal && (
        <MediaKitModal
          currentUser={currentUser}
          from="overview_kit"
          mediaKitDocuments={mediaKitDocuments}
          overviewKitDocuments={overviewKitDocuments}
          filetype={selectMediaType}
          dialogCloseHandler={() => {
            setShowMediaKitModal(false);
          }}
          selectedMediaKitHandler={handleMediaSelected}
        />
      )}
      {showSkillsHelpModal &&
        <HelpSkillsModal dialogCloseHandler={function (): void {
          setshowSkillsHelpModal(false);
        }} />
      }
      {/* {showMediaBig != null && (
        <>
          <ViewMediaModel
            kit_document={showMediaBig}
            dialogCloseHandler={function (): void {
              setshowMediaBig(null);
            }}
          />
        </>
      )} */}

    </>
  );
};

export default OverviewContent;
